import { Suspense, useState } from "react";
import { Portal, Typography } from "@mui/material";
import { Map } from "leaflet";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import {
  useMeasureConfigs,
  useUpdateMeasureConfigs,
  useUploadMeasures,
} from "hooks/measureConfigHooks";
import { useStudyArea } from "hooks/studyAreaHooks";
import { useSelectedMeasure } from "hooks/measureHooks";
import { useNavigate } from "hooks/routerHooks";

import { ErrorFormat, deserializeErrors } from "utils/ErrorUtils";
import { MeasureConf } from "types/types";

import { UploadMeasureFooter } from "components/footer/UploadMeasureFooter";
import { RenderStudyArea } from "components/map/layers/RenderStudyArea";
import { RenderMeasureConfigs } from "components/measures/addMeasurePage/RenderMeasureConfigs";
import { UploadMeasure } from "components/upload/UploadMeasure";
import { UploadedMeasureConfigs } from "components/upload/UploadedMeasureConfigs";
import { AddUploadedMeasuresFooter } from "components/footer/AddUploadedMeasuresFooter";

interface Props {
  mapRef: Map;
  uuid: string;
  footerRef: HTMLDivElement;
}

export function UploadMeasureGuard(props: Props) {
  const { mapRef, footerRef, uuid } = props;
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { mutateAsync: update, isLoading: isLoadingUpdateMeasures } =
    useUpdateMeasureConfigs(uuid);

  const [updatedExistingMeasureConfigs, setUpdatedExistingMeasureConfigs] =
    useState<MeasureConf[]>([]);

  const { data: measureConfigs } = useMeasureConfigs(uuid);
  const { data: studyArea } = useStudyArea(uuid);

  const selectedMeasure = useSelectedMeasure(
    studyArea?.measureImpactList || []
  );

  const [uploadedMeasureConfigs, setUploadedMeasureConfigs] = useState<
    MeasureConf[]
  >([]);

  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [fileUploadErrors, setFileUploadErrors] = useState<ErrorFormat[]>([]);

  const { mutateAsync: upload, isLoading } = useUploadMeasures(
    uuid,
    selectedMeasure?.measure.key || ""
  );

  function resetToUploadState() {
    setUpdatedExistingMeasureConfigs([]);
    setUploadedMeasureConfigs([]);
  }

  async function handleUpload() {
    if (acceptedFiles.length === 0) return;
    const [file] = acceptedFiles;
    await upload(file, {
      onSuccess: (data) => {
        setUploadedMeasureConfigs(data.newMeasureConfigs);
        setUpdatedExistingMeasureConfigs(data.updatedMeasureConfigs);
      },
      onError: (error) => {
        const errors = deserializeErrors(error);
        setFileUploadErrors(errors);
      },
    });
  }

  async function handleSave() {
    await update(
      [...uploadedMeasureConfigs, ...updatedExistingMeasureConfigs],
      {
        onSuccess: () => {
          resetToUploadState();
          enqueueSnackbar(t("label.newMeasureAdded"), {
            variant: "success",
          });
          navigate(`/${uuid}/measures`);
        },
        onError: (error) => {
          const errors = deserializeErrors(error);
          errors.map((er) => {
            return enqueueSnackbar(er.message, {
              variant: "error",
            });
          });
        },
      }
    );
  }

  const isReadOnly = !!studyArea?.readOnly;
  if (isReadOnly) {
    return <Navigate to={`/${uuid}/measures`} />;
  }

  return (
    <>
      <Typography variant="h2" component="h1" gutterBottom>
        {t("label.uploadAMeasure")}: {selectedMeasure?.measure.name}
      </Typography>
      <Suspense>
        {selectedMeasure && uploadedMeasureConfigs.length === 0 && (
          <UploadMeasure
            geometryType={selectedMeasure?.measure.geometryType || "POLYGON"}
            selectedMeasure={selectedMeasure}
            setAcceptedFiles={setAcceptedFiles}
            fileUploadErrors={fileUploadErrors}
            setFileUploadErrors={setFileUploadErrors}
          />
        )}
      </Suspense>

      {selectedMeasure && uploadedMeasureConfigs.length > 0 && (
        <UploadedMeasureConfigs
          mapRef={mapRef}
          measureConfigs={uploadedMeasureConfigs}
          selectedMeasure={selectedMeasure.measure}
        />
      )}

      <RenderMeasureConfigs
        mapRef={mapRef}
        measureConfigs={
          updatedExistingMeasureConfigs.length > 0
            ? updatedExistingMeasureConfigs
            : measureConfigs || []
        }
      />
      {studyArea && (
        <RenderStudyArea
          mapRef={mapRef}
          studyAreaGeo={studyArea.geometry_4326}
        />
      )}
      {uploadedMeasureConfigs.length > 0 && (
        <Portal container={footerRef}>
          <AddUploadedMeasuresFooter
            isLoading={isLoadingUpdateMeasures}
            isDisabled={
              uploadedMeasureConfigs.length === 0 || isLoadingUpdateMeasures
            }
            handleSave={handleSave}
            onCancel={() => setUploadedMeasureConfigs([])}
          />
        </Portal>
      )}
      {uploadedMeasureConfigs.length === 0 && (
        <Portal container={footerRef}>
          <UploadMeasureFooter
            uuid={uuid}
            handleUpload={handleUpload}
            isDisabled={acceptedFiles.length === 0 || isLoading}
            isLoading={isLoading}
          />
        </Portal>
      )}
    </>
  );
}
